import { useState, useRef, useEffect } from "react";
import {
  Star, Phone, Send, Bot, AlertTriangle, CheckCircle2,
  Menu, X, Home, Coffee, MessageCircle, User, Bell, CreditCard,
  Star as StarIcon, FileText,
} from "lucide-react";
import type { GuestAppConfig } from "../lib/guestAppStore";

type Props = {
  struttura: GuestAppConfig;
  ospiteNome?: string;
  pagamento?: { totale: number; pagato: number; tassa: number };
  compact?: boolean;
};

// ── Bottom nav tabs ───────────────────────────────────────────────────────────
const BOTTOM_TABS = [
  { id: "struttura", label: "Struttura", Icon: Home },
  { id: "host",      label: "Host",      Icon: User },
  { id: "servizi",   label: "Servizi",   Icon: Coffee },
  { id: "chat",      label: "Chat AI",   Icon: MessageCircle },
] as const;

type TabId = typeof BOTTOM_TABS[number]["id"];

// ── Drawer menu items ─────────────────────────────────────────────────────────
const DRAWER_ITEMS = [
  { id: "segnala",    label: "Segnalazioni",      Icon: AlertTriangle, badge: "" },
  { id: "pagamenti",  label: "Pagamenti",         Icon: CreditCard,    badge: "" },
  { id: "notifiche",  label: "Notifiche",         Icon: Bell,          badge: "2" },
  { id: "recensione", label: "Lascia recensione", Icon: StarIcon,      badge: "" },
  { id: "contratto",  label: "Il mio contratto",  Icon: FileText,      badge: "" },
] as const;

type DrawerId = typeof DRAWER_ITEMS[number]["id"];

export default function GuestAppContent({ struttura, ospiteNome, pagamento, compact = false }: Props) {
  const s = compact;
  const accent = "#c87045";
  const dark = "#1a1814";
  const muted = "#7a756e";
  const bg = "#f7f5f2";

  const [tab, setTab] = useState<TabId>("struttura");
  const [drawer, setDrawer] = useState<DrawerId | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  // Servizi popup
  const [servicioPopup, setServizioPopup] = useState<null | typeof struttura.servizi[number]>(null);

  // Chat state
  const [chatMsg, setChatMsg] = useState("");
  const [messages, setMessages] = useState([
    { from: "bot", text: `Ciao${ospiteNome ? ` ${ospiteNome.split(" ")[0]}` : ""}! Sono il tuo assistente per ${struttura.nome}. Come posso aiutarti? 😊` },
  ]);
  const chatRef = useRef<HTMLDivElement>(null);

  // Segnalazione state
  const [segnalazione, setSegnalazione] = useState({ tipo: "", desc: "" });
  const [segnalatoOk, setSegnalatoOk] = useState(false);

  const openDrawer = (id: DrawerId) => { setDrawer(id); setDrawerOpen(true); };
  const closeDrawer = () => setDrawerOpen(false);

  const sendMsg = () => {
    if (!chatMsg.trim()) return;
    const msg = chatMsg.trim();
    setMessages(m => [...m, { from: "user", text: msg }]);
    setChatMsg("");
    setTimeout(() => {
      const q = msg.toLowerCase();
      let reply = `Non ho trovato questa informazione. Contatta ${struttura.host.nome} direttamente. 😊`;

      // Wi-Fi
      if (q.includes("wifi") || q.includes("wi-fi") || q.includes("password") || q.includes("internet"))
        reply = `La password Wi-Fi è: ${struttura.wifi} 📶`;
      // Check-in / Check-out
      else if (q.includes("check-out") || q.includes("checkout") || q.includes("partenz") || q.includes("quando devo uscire")) {
        const r = struttura.regole.find(r => r.toLowerCase().includes("check-out"));
        reply = r ? `🏠 ${r}` : "Vedi le regole della casa per gli orari di check-out.";
      } else if (q.includes("check-in") || q.includes("checkin") || q.includes("arriv") || q.includes("quando posso entrare")) {
        const r = struttura.regole.find(r => r.toLowerCase().includes("check-in"));
        reply = r ? `🔑 ${r}` : "Vedi le regole della casa per gli orari di check-in.";
      }
      // Servizi specifici
      else {
        const sv = struttura.servizi.find(sv => q.includes(sv.nome.toLowerCase().split(" ")[0]));
        if (sv) { reply = `${sv.emoji} **${sv.nome}**: disponibile ${sv.orario}`; }
        // Regole generiche
        else {
          const regola = struttura.regole.find(r => r.toLowerCase().split(" ").some(w => w.length > 4 && q.includes(w)));
          if (regola) reply = `📋 ${regola}`;
          // Host info
          else if (q.includes("host") || q.includes("contatt") || q.includes("telefon") || q.includes("proprietar"))
            reply = `Il tuo host è ${struttura.host.nome}. ${struttura.host.risposta}. Puoi chiamare o scrivere su WhatsApp dalla sezione Host. 📞`;
          // Knowledge base
          else if (struttura.knowledgeBase) {
            const kb = struttura.knowledgeBase.toLowerCase();
            const words = q.split(" ").filter(w => w.length > 3);
            const found = words.find(w => kb.includes(w));
            if (found) {
              const idx = kb.indexOf(found);
              const snippet = struttura.knowledgeBase.slice(Math.max(0, idx - 30), idx + 120).trim();
              reply = `📄 ${snippet}…`;
            }
          }
        }
      }
      setMessages(m => [...m, { from: "bot", text: reply }]);
    }, 700);
  };

  useEffect(() => {
    if (chatRef.current) chatRef.current.scrollTop = chatRef.current.scrollHeight;
  }, [messages]);

  // ── Sizes based on compact ─────────────────────────────────────────────────
  const sz = {
    headerPx: s ? "px-3 pt-2 pb-2.5" : "px-5 pt-4 pb-4",
    title: s ? "text-sm font-bold" : "text-lg font-bold",
    sub: s ? "text-[9px]" : "text-xs",
    p: s ? "p-3" : "p-4",
    gap: s ? "gap-1.5" : "gap-2",
    text: s ? "text-[9px]" : "text-sm",
    textXs: s ? "text-[8px]" : "text-xs",
    btn: s ? "py-1.5 text-[8px]" : "py-3 text-sm",
    bnav: s ? "h-10" : "h-14",
    bnavIcon: s ? "h-3.5 w-3.5" : "h-5 w-5",
    bnavLabel: s ? "text-[8px]" : "text-[10px]",
    menuBtn: s ? "w-6 h-6" : "w-9 h-9",
    menuIcon: s ? "h-3 w-3" : "h-5 w-5",
    drawerW: s ? "w-44" : "w-64",
    drawerItem: s ? "px-3 py-2 text-[9px]" : "px-4 py-3.5 text-sm",
    drawerIcon: s ? "h-3.5 w-3.5" : "h-5 w-5",
  };

  // ── Main tab content ───────────────────────────────────────────────────────
  const renderTab = () => {
    if (tab === "struttura") return (
      <div>
        <div className={`flex items-center justify-center ${s ? "h-24" : "h-40"}`}
          style={{ background: `linear-gradient(135deg, ${accent}33, ${dark}55)` }}>
          <span className={s ? "text-4xl" : "text-6xl"}>{struttura.emoji}</span>
        </div>
        <div className={sz.p}>
          <h3 style={{ color: dark }} className={`font-bold mb-1 ${s ? "text-xs" : "text-base"}`}>{struttura.nome}</h3>
          <p style={{ color: muted }} className={`${sz.textXs} mb-3 leading-relaxed`}>{struttura.descrizione}</p>
          <div className={`grid grid-cols-3 ${sz.gap}`}>
            {struttura.dotazioni.map(d => (
              <div key={d.label} style={{ background: bg }} className="rounded-xl p-2 text-center">
                <div className={s ? "text-base mb-0.5" : "text-xl mb-1"}>{d.emoji}</div>
                <div style={{ color: muted }} className={`leading-tight ${sz.textXs}`}>{d.label}</div>
              </div>
            ))}
          </div>
          <div className={`${s ? "mt-3" : "mt-4"}`}>
            <p style={{ color: dark }} className={`font-semibold mb-2 ${sz.textXs}`}>Regole della casa</p>
            <ul className="space-y-1.5">
              {struttura.regole.map(r => (
                <li key={r} className="flex items-start gap-1.5" style={{ color: muted }}>
                  <span className={sz.textXs}>✓</span>
                  <span className={`${sz.textXs} leading-relaxed`}>{r}</span>
                </li>
              ))}
            </ul>
          </div>
          <div style={{ background: bg }} className={`rounded-xl flex items-center justify-between ${s ? "px-3 py-2 mt-3" : "px-4 py-3 mt-4"}`}>
            <div>
              <p style={{ color: muted }} className={sz.textXs}>📶 Password Wi-Fi</p>
              <p style={{ color: dark }} className={`font-mono font-bold ${sz.text}`}>{struttura.wifi}</p>
            </div>
            <span style={{ color: accent }} className={`font-semibold ${sz.textXs}`}>Copia</span>
          </div>
        </div>
      </div>
    );

    if (tab === "servizi") return (
      <div className={sz.p}>
        <div className={`grid grid-cols-3 ${s ? "gap-1.5" : "gap-2"}`}>
          {struttura.servizi.map(sv => (
            <button key={sv.nome} onClick={() => setServizioPopup(sv)}
              style={{ background: bg }}
              className={`flex flex-col items-center text-center rounded-xl transition-opacity active:opacity-70 ${s ? "p-2" : "p-3"}`}>
              <span className={s ? "text-xl mb-1" : "text-3xl mb-1.5"}>{sv.emoji}</span>
              <div style={{ color: dark }} className={`font-semibold leading-tight ${s ? "text-[8px]" : "text-[10px]"}`}>{sv.nome}</div>
              <div style={{ color: muted }} className={`mt-0.5 ${s ? "text-[7px]" : "text-[9px]"}`}>{sv.orario}</div>
            </button>
          ))}
        </div>
        {/* Service popup */}
        {servicioPopup && (
          <div className="absolute inset-0 z-40 flex items-end justify-center"
            style={{ background: "rgba(0,0,0,0.45)" }}
            onClick={() => setServizioPopup(null)}>
            <div className={`bg-white w-full rounded-t-3xl ${s ? "p-4" : "p-5"}`}
              onClick={e => e.stopPropagation()}>
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <span className={s ? "text-2xl" : "text-3xl"}>{servicioPopup.emoji}</span>
                  <span style={{ color: dark }} className={`font-bold ${s ? "text-sm" : "text-base"}`}>{servicioPopup.nome}</span>
                </div>
                <button onClick={() => setServizioPopup(null)}
                  className="w-7 h-7 rounded-full flex items-center justify-center"
                  style={{ background: bg }}>
                  <X style={{ color: muted }} className="h-4 w-4" />
                </button>
              </div>
              <div style={{ background: bg }} className={`rounded-xl ${s ? "p-3" : "p-4"}`}>
                <div className="flex items-center gap-2">
                  <span style={{ color: muted }} className={sz.textXs}>🕐 Orario / Disponibilità</span>
                </div>
                <p style={{ color: dark }} className={`font-semibold mt-1 ${sz.text}`}>{servicioPopup.orario}</p>
              </div>
              <p style={{ color: muted }} className={`mt-3 ${sz.textXs} leading-relaxed`}>
                Per informazioni o prenotazione contatta l'host dalla sezione <strong>Host</strong>.
              </p>
            </div>
          </div>
        )}
      </div>
    );

    if (tab === "host") return (
      <div className={sz.p}>
        <div style={{ background: bg }} className={`rounded-2xl text-center ${s ? "p-3 mb-3" : "p-5 mb-4"}`}>
          <div style={{ background: `${accent}22`, color: accent }}
            className={`rounded-2xl flex items-center justify-center font-bold mx-auto mb-2 ${s ? "w-12 h-12 text-lg" : "w-20 h-20 text-3xl"}`}>
            {struttura.host.initials}
          </div>
          <div style={{ color: dark }} className={`font-bold ${sz.text}`}>{struttura.host.nome}</div>
          <div style={{ color: muted }} className={sz.textXs}>{struttura.host.ruolo}</div>
          <div className={`flex items-center justify-center gap-0.5 ${s ? "mt-1" : "mt-1.5"}`}>
            {[1,2,3,4,5].map(i => <Star key={i} className={`fill-amber-400 text-amber-400 ${s ? "h-2.5 w-2.5" : "h-3.5 w-3.5"}`} />)}
            <span style={{ color: muted }} className={`ml-1 ${sz.textXs}`}>{struttura.host.rating}</span>
          </div>
        </div>
        <p style={{ color: muted }} className={`text-center mb-2 leading-relaxed ${sz.textXs}`}>{struttura.host.bio}</p>
        <p style={{ color: muted }} className={`text-center mb-3 italic ${sz.textXs}`}>{struttura.host.risposta}</p>
        <div className={`grid grid-cols-2 ${sz.gap}`}>
          <a href={`https://wa.me/${struttura.host.whatsapp}`} target="_blank" rel="noopener noreferrer"
            className={`flex items-center justify-center gap-1.5 bg-[#25D366] text-white font-semibold rounded-xl ${sz.btn}`}>
            💬 WhatsApp
          </a>
          <a href={`tel:${struttura.host.telefono}`}
            style={{ background: dark, color: "white" }}
            className={`flex items-center justify-center gap-1.5 font-semibold rounded-xl ${sz.btn}`}>
            <Phone className={s ? "h-2.5 w-2.5" : "h-3.5 w-3.5"} /> Chiama
          </a>
        </div>
      </div>
    );

    if (tab === "chat") return (
      <div className="flex flex-col" style={{ height: s ? "260px" : "420px" }}>
        <div ref={chatRef} className={`flex-1 overflow-y-auto space-y-2 ${sz.p}`}>
          {messages.map((m, i) => (
            <div key={i} className={`flex ${m.from === "user" ? "justify-end" : "justify-start"} gap-1.5`}>
              {m.from === "bot" && (
                <div style={{ background: accent }} className={`rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ${s ? "w-5 h-5" : "w-7 h-7"}`}>
                  <Bot className={`text-white ${s ? "h-2.5 w-2.5" : "h-4 w-4"}`} />
                </div>
              )}
              <div className={`max-w-[78%] rounded-2xl leading-relaxed ${s ? "px-2.5 py-1.5 text-[9px]" : "px-3.5 py-2.5 text-xs"}`}
                style={m.from === "user"
                  ? { background: dark, color: "white", borderTopRightRadius: 4 }
                  : { background: bg, color: dark, borderTopLeftRadius: 4 }}>
                {m.text}
              </div>
            </div>
          ))}
        </div>
        <div className={`border-t border-gray-100 flex ${s ? "gap-1.5 p-2" : "gap-2 p-3"}`}>
          <input value={chatMsg} onChange={e => setChatMsg(e.target.value)}
            onKeyDown={e => e.key === "Enter" && sendMsg()}
            className={`flex-1 border rounded-xl focus:outline-none bg-white ${s ? "px-2 py-1.5 text-[9px]" : "px-3 py-2.5 text-sm"}`}
            style={{ borderColor: "#e8e3dc" }}
            placeholder="Chiedi qualcosa…" />
          <button onClick={sendMsg} style={{ background: accent }}
            className={`rounded-xl flex items-center justify-center flex-shrink-0 ${s ? "w-7 h-7" : "w-10 h-10"}`}>
            <Send className={`text-white ${s ? "h-3 w-3" : "h-4 w-4"}`} />
          </button>
        </div>
      </div>
    );
    return null;
  };

  // ── Drawer content ─────────────────────────────────────────────────────────
  const renderDrawer = () => {
    if (!drawer) return null;

    if (drawer === "segnala") return (
      <div className={`${sz.p} space-y-2`}>
        <p style={{ color: dark }} className={`font-bold mb-3 ${sz.text}`}>⚠️ Segnalazioni</p>
        {segnalatoOk ? (
          <div className="text-center py-4">
            <CheckCircle2 className="mx-auto mb-2 text-emerald-500" style={{ width: s ? 28 : 40, height: s ? 28 : 40 }} />
            <p style={{ color: dark }} className={`font-semibold ${sz.text}`}>Inviata!</p>
            <button onClick={() => { setSegnalatoOk(false); setSegnalazione({ tipo: "", desc: "" }); }}
              style={{ color: accent }} className={`mt-2 underline font-semibold ${sz.textXs}`}>Nuova</button>
          </div>
        ) : (
          <>
            <select value={segnalazione.tipo} onChange={e => setSegnalazione(p => ({ ...p, tipo: e.target.value }))}
              className={`w-full rounded-xl px-3 appearance-none focus:outline-none bg-white ${s ? "py-1.5 text-[9px]" : "py-2.5 text-sm"}`}
              style={{ border: "1px solid #e8e3dc" }}>
              <option value="">— Tipo problema —</option>
              {["Guasto elettrico","Idraulico","Elettrodomestico","Pulizia","Wi-Fi","Altro"].map(t => <option key={t}>{t}</option>)}
            </select>
            <textarea value={segnalazione.desc} onChange={e => setSegnalazione(p => ({ ...p, desc: e.target.value }))}
              className={`w-full rounded-xl px-3 resize-none focus:outline-none bg-white ${s ? "py-1.5 text-[9px] h-16" : "py-2.5 text-sm h-24"}`}
              style={{ border: "1px solid #e8e3dc" }} placeholder="Descrivi il problema…" />
            <button onClick={() => segnalazione.tipo && segnalazione.desc && setSegnalatoOk(true)}
              disabled={!segnalazione.tipo || !segnalazione.desc}
              style={{ background: dark, opacity: (!segnalazione.tipo || !segnalazione.desc) ? 0.4 : 1 }}
              className={`w-full text-white font-semibold rounded-xl ${sz.btn}`}>
              Invia
            </button>
          </>
        )}
      </div>
    );

    if (drawer === "pagamenti") return (
      <div className={`${sz.p} space-y-2`}>
        <p style={{ color: dark }} className={`font-bold mb-2 ${sz.text}`}>💳 Pagamenti</p>
        {pagamento ? (
          <>
            <div style={{ background: bg }} className={`rounded-xl ${s ? "p-3 space-y-1.5" : "p-4 space-y-2"}`}>
              {[
                ["Pernottamento", `€ ${pagamento.totale}`, dark],
                ["Pagato", `− € ${pagamento.pagato}`, "#059669"],
                ...(pagamento.tassa > 0 ? [["Tassa soggiorno", `€ ${pagamento.tassa}`, "#d97706"]] : []),
              ].map(([label, val, color]) => (
                <div key={label as string} className="flex justify-between">
                  <span style={{ color: muted }} className={sz.textXs}>{label}</span>
                  <span style={{ color: color as string }} className={`font-semibold ${sz.textXs}`}>{val}</span>
                </div>
              ))}
              <div className="flex justify-between pt-1.5 border-t border-gray-200">
                <span style={{ color: dark }} className={`font-bold ${sz.text}`}>Da pagare</span>
                <span className={`font-bold ${sz.text}`}
                  style={{ color: (pagamento.totale - pagamento.pagato + pagamento.tassa) === 0 ? "#059669" : "#FF5A5F" }}>
                  {(pagamento.totale - pagamento.pagato + pagamento.tassa) === 0 ? "€ 0 ✅" : `€ ${pagamento.totale - pagamento.pagato + pagamento.tassa}`}
                </span>
              </div>
            </div>
          </>
        ) : (
          <p style={{ color: muted }} className={sz.textXs}>Nessun pagamento da mostrare.</p>
        )}
      </div>
    );

    if (drawer === "notifiche") return (
      <div className={sz.p}>
        <p style={{ color: dark }} className={`font-bold mb-3 ${sz.text}`}>🔔 Notifiche</p>
        {[
          { ico: "📋", titolo: "Check-in confermato", tempo: "2h fa", letto: false },
          { ico: "🔑", titolo: "Istruzioni accesso inviate", tempo: "ieri", letto: false },
          { ico: "📍", titolo: "Struttura aggiunta alla mappa", tempo: "3gg fa", letto: true },
        ].map((n, i) => (
          <div key={i} className={`flex items-start gap-2 border-b py-2.5 ${i === 0 ? "" : ""}`}
            style={{ borderColor: "#f0ece8" }}>
            <span className={s ? "text-base" : "text-lg"}>{n.ico}</span>
            <div className="flex-1">
              <p style={{ color: dark }} className={`font-medium ${sz.textXs} ${!n.letto ? "font-semibold" : ""}`}>{n.titolo}</p>
              <p style={{ color: muted }} className={`${sz.textXs} opacity-70`}>{n.tempo}</p>
            </div>
            {!n.letto && <div className="w-2 h-2 rounded-full mt-1 flex-shrink-0" style={{ background: accent }} />}
          </div>
        ))}
      </div>
    );

    if (drawer === "recensione") return (
      <div className={`${sz.p} text-center`}>
        <p style={{ color: dark }} className={`font-bold mb-2 ${sz.text}`}>⭐ Lascia una recensione</p>
        <p style={{ color: muted }} className={`mb-4 ${sz.textXs}`}>Come stai trovando il soggiorno?</p>
        <div className={`flex justify-center mb-4 ${s ? "gap-2" : "gap-3"}`}>
          {[1,2,3,4,5].map(i => (
            <button key={i} className={s ? "text-2xl" : "text-4xl"}>⭐</button>
          ))}
        </div>
        <textarea className={`w-full rounded-xl px-3 resize-none focus:outline-none bg-white text-left ${s ? "py-1.5 text-[9px] h-16" : "py-2.5 text-sm h-20"}`}
          style={{ border: "1px solid #e8e3dc" }} placeholder="Scrivi la tua recensione…" />
        <button style={{ background: accent }}
          className={`w-full text-white font-semibold rounded-xl mt-3 ${sz.btn}`}>
          Invia recensione
        </button>
      </div>
    );

    if (drawer === "contratto") return (
      <div className={sz.p}>
        <p style={{ color: dark }} className={`font-bold mb-3 ${sz.text}`}>📋 Il mio contratto</p>
        <div style={{ background: bg }} className={`rounded-xl ${s ? "p-2.5" : "p-3"} mb-3`}>
          <div className="flex items-center gap-2 mb-1">
            <FileText style={{ color: accent }} className={sz.drawerIcon} />
            <span style={{ color: dark }} className={`font-semibold ${sz.textXs}`}>Contratto di Locazione Turistica</span>
          </div>
          <p style={{ color: muted }} className={sz.textXs}>Firmato il {new Date().toLocaleDateString("it-IT")}</p>
        </div>
        <div className="flex items-center gap-2">
          <CheckCircle2 className={`text-emerald-500 ${sz.drawerIcon}`} />
          <span style={{ color: muted }} className={sz.textXs}>Firma digitale apposta</span>
        </div>
      </div>
    );

    return null;
  };

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div className="flex flex-col h-full relative overflow-hidden bg-white">

      {/* Top header */}
      <div style={{ background: dark }} className={`flex-shrink-0 flex items-center justify-between ${sz.headerPx}`}>
        <div>
          <div style={{ color: "rgba(255,255,255,0.55)" }} className={sz.textXs}>Benvenuto</div>
          <div style={{ color: "white" }} className={sz.title}>{struttura.nome}</div>
          <div style={{ color: "rgba(255,255,255,0.6)" }} className={`${sz.textXs} mt-0.5`}>📍 {struttura.location}</div>
        </div>
        <button onClick={() => drawerOpen ? closeDrawer() : openDrawer("segnala")}
          style={{ background: "rgba(255,255,255,0.12)" }}
          className={`${sz.menuBtn} rounded-xl flex items-center justify-center flex-shrink-0`}
          aria-label="Menu">
          <Menu className={`text-white ${sz.menuIcon}`} />
        </button>
      </div>

      {/* Main scrollable content */}
      <div className="flex-1 overflow-y-auto bg-white">
        {renderTab()}
      </div>

      {/* Bottom nav */}
      <div className={`flex-shrink-0 flex border-t bg-white ${sz.bnav}`} style={{ borderColor: "#ede9e4" }}>
        {BOTTOM_TABS.map(({ id, label, Icon }) => {
          const active = tab === id;
          return (
            <button key={id} onClick={() => { setTab(id); setDrawerOpen(false); }}
              className="flex-1 flex flex-col items-center justify-center gap-0.5 transition-colors"
              style={{ color: active ? accent : "#aaa" }}>
              <Icon className={sz.bnavIcon} />
              <span className={`font-medium ${sz.bnavLabel}`}>{label}</span>
            </button>
          );
        })}
      </div>

      {/* Hamburger drawer overlay */}
      {drawerOpen && (
        <div className="absolute inset-0 z-30 flex" style={{ background: "rgba(0,0,0,0.35)" }}
          onClick={closeDrawer}>
          {/* Drawer panel — slides from right */}
          <div className={`absolute right-0 top-0 bottom-0 ${sz.drawerW} bg-white flex flex-col shadow-2xl`}
            onClick={e => e.stopPropagation()}>
            {/* Drawer header */}
            <div style={{ background: dark }} className={`flex items-center justify-between flex-shrink-0 ${s ? "px-3 py-2.5" : "px-4 py-3.5"}`}>
              <span style={{ color: "white" }} className={`font-semibold ${sz.text}`}>Menu</span>
              <button onClick={closeDrawer} className="w-6 h-6 flex items-center justify-center rounded-lg"
                style={{ background: "rgba(255,255,255,0.15)" }}>
                <X className={`text-white ${s ? "h-3 w-3" : "h-4 w-4"}`} />
              </button>
            </div>

            {/* Drawer nav items */}
            <div className="flex-shrink-0 border-b" style={{ borderColor: "#f0ece8" }}>
              {DRAWER_ITEMS.map(({ id, label, Icon, badge }) => (
                <button key={id} onClick={() => setDrawer(id as DrawerId)}
                  className={`w-full flex items-center gap-3 ${sz.drawerItem} transition-colors text-left`}
                  style={{
                    background: drawer === id ? `${accent}12` : "white",
                    color: drawer === id ? accent : dark,
                    borderBottom: "1px solid #f5f0ec",
                  }}>
                  <Icon className={sz.drawerIcon} />
                  <span className="flex-1 font-medium">{label}</span>
                  {badge && (
                    <span style={{ background: accent }} className={`text-white font-bold rounded-full leading-none ${s ? "text-[7px] w-3.5 h-3.5" : "text-[9px] w-5 h-5"} flex items-center justify-center`}>
                      {badge}
                    </span>
                  )}
                </button>
              ))}
            </div>

            {/* Drawer content area */}
            <div className="flex-1 overflow-y-auto">
              {renderDrawer()}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
